import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setFeatures } from 'store/settings/featuresSlice'
import { apiGetFeatures, apiUpdateFeatures } from 'services/SettingService'
import { DEFAULT_FEATURES, FEATURE_CASHOUT } from 'constants/features.constant'

// Read-only: the current on/off map, no fetch side-effect. `?.` for the same
// stale-cache reason as useAnnouncementValue.
export function useFeatureFlags() {
    return useSelector((state) => state.settings.features?.value) || DEFAULT_FEATURES
}

export function useFeatureEnabled(feature) {
    return useFeatureFlags()[feature] !== false
}

// The payout/commission withdrawal switches pause the USDT (crypto) rails
// only; Bank/Ewallet follows the separate cashout switch. A member can still
// request while either one is open. The backend enforces both.
export function useWithdrawalRails(feature) {
    const flags = useFeatureFlags()
    const usdtOpen = flags[feature] !== false
    // Cashout defaults OFF (DEFAULT_FEATURES), so only an explicit true counts.
    const bankEwalletOpen = flags[FEATURE_CASHOUT] === true
    return { usdtOpen, bankEwalletOpen, anyOpen: usdtOpen || bankEwalletOpen }
}

// One revalidation per page load. This can't rely on `loaded`: App wraps
// everything in PersistGate, so the persisted `loaded: true` lands before
// anything mounts and overwrites the slice's REHYDRATE reset, and the fetch
// was skipped forever after the first visit. That left a browser blind to
// later changes, e.g. My Wallet staying visible after cashout was switched off.
let revalidatedThisLoad = false

// Fetch-on-mount + read + update. Only call this from the top-level Layout
// and the settingsdb card -- see the same rule for useSiteName.
function useFeatures() {

    const dispatch = useDispatch()

    const loaded = useSelector((state) => state.settings.features?.loaded)
    const features = useFeatureFlags()

    useEffect(() => {
        if (revalidatedThisLoad) {
            return
        }
        revalidatedThisLoad = true
        apiGetFeatures().then(resp => {
            dispatch(setFeatures(resp.data.features))
        }).catch(() => {
            // Let a later mount try again.
            revalidatedThisLoad = false
        })
    }, [dispatch])

    const updateFeatures = async (changes) => {
        const resp = await apiUpdateFeatures(changes)
        dispatch(setFeatures(resp.data.features))
        return resp.data.features
    }

    return { features, loaded, updateFeatures }
}

export default useFeatures
